import { CheckCircle2, Clock, AlertOctagon, DollarSign, FileX } from 'lucide-react';

/**
 * Iter 235.e (Carlos): Badge unificado de estado de una factura en el ciclo
 * DTE → ERP → Alegra → Pago.
 *
 * Reglas de prioridad (de más específico a más genérico):
 *   1. `eliminada_en_alegra` → 🔴 ELIMINADA EN ALEGRA
 *   2. `estado_pago === "pagada"` → 🟢 PAGADA
 *   3. `contabilizada_en_alegra` (bill_id en alegra_bills) → 🔵 EN ALEGRA
 *      - Si además tiene `estado_pago === "pendiente"` → "EN ALEGRA · Pdte pago"
 *   4. `contabilizada` (solo en ERP, no Alegra) → 🟣 CONTAB. ERP
 *   5. Default → ⚪ DTE RECIBIDO
 *
 * Props:
 *   - contabilizada_en_alegra: boolean
 *   - contabilizada: boolean
 *   - estado_pago: 'pendiente' | 'pagada' | ''
 *   - eliminada_en_alegra: boolean (default false)
 *   - alegra_bill_id: string (opcional, para tooltip)
 *   - en_nomina: boolean (opcional)
 *   - liberada_para_pago: boolean (opcional)
 *   - size: 'xs' | 'sm' (default 'sm')
 */
export function AlegraStatusBadge({
  contabilizada_en_alegra = false,
  contabilizada = false,
  estado_pago = '',
  eliminada_en_alegra = false,
  alegra_bill_id = '',
  en_nomina = false,
  liberada_para_pago = false,
  size = 'sm',
}) {
  const sizeClass = size === 'xs'
    ? 'text-[8px] px-1 py-0'
    : 'text-[9px] px-1.5 py-0.5';
  const iconSize = size === 'xs' ? 'w-2 h-2' : 'w-2.5 h-2.5';

  let config;

  if (eliminada_en_alegra) {
    config = {
      label: 'ELIMINADA EN ALEGRA',
      icon: <FileX className={iconSize} />,
      cls: 'bg-red-100 text-red-800 border-red-300',
      title: `El bill ${alegra_bill_id} fue eliminado en Alegra. Verificado contra API live.`,
    };
  } else if (estado_pago === 'pagada') {
    config = {
      label: 'PAGADA',
      icon: <DollarSign className={iconSize} />,
      cls: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      title: `Factura pagada${alegra_bill_id ? ` · bill ${alegra_bill_id}` : ''}.`,
    };
  } else if (contabilizada_en_alegra) {
    const tieneEstado = !!estado_pago;
    const subLabel = tieneEstado
      ? (estado_pago === 'pendiente' ? ' · Pdte pago' : ` · ${estado_pago}`)
      : '';
    config = {
      label: `EN ALEGRA${subLabel}`,
      icon: <CheckCircle2 className={iconSize} />,
      cls: 'bg-blue-100 text-blue-800 border-blue-300',
      title: `Contabilizada en Alegra (bill ${alegra_bill_id || '—'})${tieneEstado ? `. Estado pago: ${estado_pago}` : ''}.`,
    };
  } else if (contabilizada) {
    config = {
      label: 'CONTAB. ERP',
      icon: <CheckCircle2 className={iconSize} />,
      cls: 'bg-purple-100 text-purple-800 border-purple-300',
      title: 'Registrada en contabilizadas_oc pero aún sin alegra_bill_id confirmado.',
    };
  } else {
    config = {
      label: 'DTE RECIBIDO',
      icon: <Clock className={iconSize} />,
      cls: 'bg-gray-100 text-gray-700 border-gray-300',
      title: 'DTE recibido pero no contabilizado aún.',
    };
  }

  // Sub-badges informativos (nomina / liberada)
  return (
    <span className="inline-flex items-center gap-0.5 flex-wrap">
      <span
        className={`inline-flex items-center gap-0.5 rounded border font-bold whitespace-nowrap ${sizeClass} ${config.cls}`}
        title={config.title}
      >
        {config.icon}
        {config.label}
      </span>
      {en_nomina && (estado_pago !== 'pagada') && (
        <span
          className={`inline-flex items-center rounded border bg-indigo-50 text-indigo-700 border-indigo-200 ${sizeClass}`}
          title="Asignada a nómina de pago"
        >
          📋 nómina
        </span>
      )}
      {liberada_para_pago && (estado_pago === 'pendiente') && (
        <span
          className={`inline-flex items-center rounded border bg-cyan-50 text-cyan-700 border-cyan-200 ${sizeClass}`}
          title="Liberada para pago (VB Gerencia aplicado)"
        >
          ✓ VB
        </span>
      )}
    </span>
  );
}

// Utilidad de iconos cuando se necesitan combinaciones
export function AlegraInlineBadge({ contabilizada_en_alegra, alegra_bill_id }) {
  if (!contabilizada_en_alegra) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[8px] font-bold"
      title={`En Alegra (bill ${alegra_bill_id || '—'})`}
    >
      <CheckCircle2 className="w-2 h-2" /> Alegra
    </span>
  );
}

// Sugerencia de filtros
export const ALEGRA_STATUS_FILTERS = [
  { key: 'all', label: 'Todas' },
  { key: 'en_alegra', label: 'En Alegra' },
  { key: 'pdte_pago', label: 'Pendiente pago' },
  { key: 'pagada', label: 'Pagada' },
  { key: 'no_contab', label: 'No contabilizada' },
  { key: 'eliminada', label: 'Eliminada Alegra' },
];

// Stub para mostrar otro icono cuando hay alegra_id pero sin estado conocido
export { AlertOctagon };
